import { useState } from "react";
import { useNavigate } from "react-router-dom";
import QuickStartStep from "./components/QuickStartStep";
import UploadStep from "./components/UploadStep";
import ProcessingStep from "./components/ProcessingStep";
import ResultStep from "./components/ResultStep";
import ConversionStep from "./components/ConversionStep";

type Step = "quickstart" | "upload" | "processing" | "result" | "conversion";

const STEP_LABELS = ["시작", "업로드", "AI 처리", "결과 확인", "배포하기"];
const STEP_KEYS: Step[] = ["quickstart", "upload", "processing", "result", "conversion"];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("quickstart");
  const [isDemo, setIsDemo] = useState(false);

  const stepIndex = STEP_KEYS.indexOf(step);

  const handleUploadOption = () => {
    setIsDemo(false);
    setStep("upload");
  };

  const handleDemoOption = () => {
    setIsDemo(true);
    setStep("processing");
  };

  return (
    <div className="min-h-screen bg-[#0A0E14] flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/6">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/35b72bf3-4b7f-4858-926b-243fdf4afa99.png"
            alt="StreamClip AI"
            className="h-8 w-8 object-contain"
          />
          <span
            className="text-white font-bold text-base tracking-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            StreamClip<span className="text-[#00D9FF]">AI</span>
          </span>
        </button>

        {/* Step indicator */}
        <div className="hidden md:flex items-center gap-1">
          {STEP_LABELS.map((label, i) => {
            const isActive = i === stepIndex;
            const isDone = i < stepIndex;
            return (
              <div key={label} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isActive
                    ? "bg-[#00D9FF]/15 border border-[#00D9FF]/40 text-[#00D9FF]"
                    : isDone
                    ? "text-[#10B981]"
                    : "text-gray-600"
                }`} style={{ fontFamily: "'Inter', sans-serif" }}>
                  {isDone ? (
                    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#10B981]/20">
                      <i className="ri-check-line text-[#10B981] text-xs" />
                    </span>
                  ) : (
                    <span className={`w-4 h-4 flex items-center justify-center rounded-full text-xs font-bold ${isActive ? "bg-[#00D9FF]/20 text-[#00D9FF]" : "bg-white/5 text-gray-600"}`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {i + 1}
                    </span>
                  )}
                  {label}
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div className={`w-5 h-px mx-0.5 ${i < stepIndex ? "bg-[#10B981]/40" : "bg-white/8"}`} />
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={() => navigate("/")}
          className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          나중에 하기
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        {step === "quickstart" && (
          <QuickStartStep onUpload={handleUploadOption} onDemo={handleDemoOption} />
        )}
        {step === "upload" && (
          <UploadStep
            onUploaded={() => setStep("processing")}
            onBack={() => setStep("quickstart")}
          />
        )}
        {step === "processing" && (
          <ProcessingStep isDemo={isDemo} onComplete={() => setStep("result")} />
        )}
        {step === "result" && (
          <ResultStep onContinue={() => setStep("conversion")} />
        )}
        {step === "conversion" && (
          <ConversionStep onExplore={() => navigate("/")} />
        )}
      </main>

      {/* Bottom progress bar */}
      <div className="h-0.5 bg-white/5">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${((stepIndex + 1) / STEP_KEYS.length) * 100}%`,
            background: "linear-gradient(90deg, #00D9FF, #10B981)",
          }}
        />
      </div>
    </div>
  );
}
